<script setup lang="ts">
import { useActorStore } from '@/stores/actor'
import { useSubscriptionStore } from '@/stores/subscription'
import { useQueryClient } from '@tanstack/vue-query'
import { useMagicKeys, whenever } from '@vueuse/core'
import { computed, reactive, ref } from 'vue'

const actorStore = useActorStore()
const subscriptionStore = useSubscriptionStore()
const queryClient = useQueryClient()

const toggleModal = (modalName: keyof typeof modals, value: boolean) => {
  modals[modalName] = value
}
const changeActor = (actorId: string) => {
  actorStore.changeActor(actorId)
  queryClient.removeQueries({ type: 'all' })
  toggleModal('actor', false)
}

const modals = reactive({ actor: false })
const _keyListener = () => {
  if (subscriptionStore.subscriberGroups.length > 1) {
    actorStore.switchingActor = false
    toggleModal('actor', true)
  }
}
const searchStr = ref('')

const { Ctrl_G, Meta_G } = useMagicKeys()
whenever(Ctrl_G, _keyListener)
whenever(Meta_G, _keyListener)

const sgActorsList = computed(() => {
  const sgActorGroups = actorStore.getGroupedActors
  if (!sgActorGroups) return {}
  const matchingSgs = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(sgActorGroups).filter(([name, actors]) => {
      return name.toLowerCase().includes(searchStr.value.toLowerCase())
    })
  )
  return matchingSgs
})
</script>
<template>
  <b-modal
    v-model="modals.actor"
    has-modal-card
    trap-focus
    auto-focus
    :can-cancel="['escape', 'outside']"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ $t('switch-actor') }}
        </p>
        <button
          type="button"
          class="delete"
          :aria-label="`${$t('close')}`"
          @click.stop.prevent="toggleModal('actor', false)"
          @keydown.enter.stop.prevent="toggleModal('actor', false)"
        />
      </header>
      <section class="modal-card-body">
        <b-field class="name-input">
          <b-input v-model="searchStr"></b-input>
        </b-field>
        <div class="search-results">
          <b-collapse
            v-for="(sg, sgName) of sgActorsList"
            :key="sgName"
            class="card"
            animation="slide"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">{{ sgName }}</p>
                <span class="card-header-icon">
                  <b-icon :icon="props.open ? 'chevron-down' : 'chevron-up'"> </b-icon>
                </span>
              </div>
            </template>
            <template #default>
              <div v-for="actor in sg" :key="actor.id" class="card-content">
                <b-button
                  class="actor-list-item"
                  type="is-text"
                  :disabled="actorStore.switchingActor"
                  @click.stop.prevent="changeActor(actor.id)"
                  @keydown.enter.stop.prevent="changeActor(actor.id)"
                >
                  {{ actor.name }}
                </b-button>
              </div>
            </template>
          </b-collapse>
        </div>
      </section>
    </div>
  </b-modal>
</template>
<style lang="scss" scoped>
section.modal-card-body {
  display: grid;
  gap: 0.5rem;
  & > div.search-results {
    padding: 0rem 0rem;
    overflow-y: auto;
  }
}
.card {
  .card-header {
    .card-header-title {
      padding: 0.25rem 0.5rem;
    }
  }
  .collapse-content {
    .card-content {
      padding: 0.25rem;
    }
  }
}
</style>
